import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { find, first } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import {
  ColumnWidths,
  FormLayoutItem,
  FormLayoutRow,
} from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.constants';
import {
  AIOnboardingModalFormFieldConfiguration,
  AIOnboardingModalFormShape,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { hasValidPrincipalSelection } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/entities.fields.utils';
import {
  type CreatableOwnerEntityOption,
  type CreatablePrincipalPersonOption,
  useOnboardingEntityPrincipalOptions,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/hooks/useOnboardingEntityPrincipalOptions';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { FetchAiOnboardingModalDataV2Document } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModalV2.generated';
import { SuggestionNotesHelp } from '@/modules/aiSuggestions/components/SuggestionNotesHelp';
import { SharedIntegrationEntityTypeahead } from '@/modules/assetProviderIntegrations/shared/SharedIntegrationEntityTypeahead';
import { determineDefaultAssetClassId } from '@/modules/assets/utils';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { EntityDocumentViewerDocument } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { useAssetCategoryOptions } from '@/modules/entities/StructuredAssetsSubform/hooks/useAssetCategoryOptions';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useDefaultAssetClassId } from '@/modules/tenant/TenantDetailsContext/hooks/useDefaultAssetClassId';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { COLORS } from '@/styles/tokens/colors';
import { PathsOf } from '@/types/subform';
import { diagnostics } from '@/utils/diagnostics';
import { getEntitySelectOptions } from '@/utils/entities/getEntitySelectOptions';
import { getPulidKind, PulidKind } from '@/utils/pulid';

interface EntityFieldsRowProps {
  index: number;
  suggestionID: string | undefined;
}

export function EntityFieldsRow({ index, suggestionID }: EntityFieldsRowProps) {
  const configs = AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS.entities;
  const { columns, styles } = configs;

  const {
    household: { id: householdId },
    suggestionsByID,
    principalNodesById,
  } = useAIOnboardingModalContext();

  const { primaryClients, possibleGrantors } = useHouseholdDetailsContext();

  const entityTypeOptions = useMemo(
    // Remove GRAT from the entity type options until we get rid of the "Implementation" GRAT step
    () => getEntitySelectOptions().filter((o) => o.value !== 'grat'),
    []
  );

  // Track suggestions that have already been created as ClientProfiles so we
  // don't offer the "Add <person>" option again.
  const [alreadyCreatedSuggestions, setAlreadyCreatedSuggestions] = useState<
    string[]
  >([]);

  // Track KGV2 owner nodes that have been converted into real entities so we
  // don't continue to show the "Add <entity>" option.
  const [
    alreadyCreatedOwnerEntityNodeNames,
    setAlreadyCreatedOwnerEntityNodeNames,
  ] = useState<string[]>([]);

  // Declare modal state before the memoized options and creation callbacks.
  const [isClientProfileModalOpen, setClientProfileModalOpen] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] =
    useState<CreatablePrincipalPersonOption | null>(null);
  const [isEntityModalOpen, setEntityModalOpen] = useState(false);
  const [pendingEntity, setPendingEntity] =
    useState<CreatableOwnerEntityOption | null>(null);

  const { control, setValue } = useFormContext<AIOnboardingModalFormShape>();
  const suggestionNotes = suggestionID
    ? suggestionsByID[suggestionID]?.onboardingEntity?.notes
    : undefined;

  const entityType = useWatch({
    control,
    name: `entities.${index}.entityType` as const satisfies PathsOf<AIOnboardingModalFormShape>,
  });

  const entityFieldValues =
    useWatch({
      control,
      name: 'entities',
    }) || [];

  const {
    allowMultiplePrincipals,
    clientProfileOptions,
    suggestedPrincipalOptions,
    businessEntityOwnerEntityOptions,
    creatableOwnerEntityOptions,
    existingEntityOptions,
  } = useOnboardingEntityPrincipalOptions({
    possiblePrimaryClients: primaryClients || [],
    possibleGrantors: possibleGrantors || [],
    suggestionsByID,
    suggestionID,
    entityType,
    entityFieldValues,
    householdId,
    principalNodesById,
  });

  const richOptions = useMemo(() => {
    const options: TypeaheadSelectInputOption<string>[] = [];

    // 1. Suggested principals – selectable, with plus icon and quotes
    suggestedPrincipalOptions.forEach((opt) => {
      if (alreadyCreatedSuggestions.includes(opt.value)) {
        return;
      }

      options.push({
        display: opt.display,
        value: `add-person-${opt.value}`,
        onSelectAction: () => {
          diagnostics.debug('[EntityFieldsRow] clicked add new', { opt });
          setPendingSuggestion(opt);
          setClientProfileModalOpen(true);
        },
        renderOption: (props) => (
          <MenuItem {...props} sx={(theme) => ({ height: theme.spacing(5) })}>
            <ListItemIcon>
              <PlusIcon size={16} />
            </ListItemIcon>
            <ListItemText>{`Add "${opt.display}"`}</ListItemText>
          </MenuItem>
        ),
      });
    });

    // 2. "Add <entity>" options for KGV2 owners that are not visible rows.
    creatableOwnerEntityOptions.forEach((opt) => {
      if (alreadyCreatedOwnerEntityNodeNames.includes(opt.nodeName)) {
        return;
      }

      options.push({
        display: opt.display,
        value: `add-entity-${opt.value}`,
        onSelectAction: () => {
          diagnostics.debug('[EntityFieldsRow] clicked add new entity', {
            opt,
          });
          setPendingEntity(opt);
          setEntityModalOpen(true);
        },
        renderOption: (props) => (
          <MenuItem {...props} sx={(theme) => ({ height: theme.spacing(5) })}>
            <ListItemIcon>
              <PlusIcon size={16} />
            </ListItemIcon>
            <ListItemText>{`Add "${opt.display}"`}</ListItemText>
          </MenuItem>
        ),
      });
    });

    // 3. Group label for Individuals
    if (clientProfileOptions.length) {
      options.push(
        ...clientProfileOptions.map((option) => ({
          ...option,
          groupName: 'Individuals',
        }))
      );
    }

    // 4. Group label for Entities (existing suggestions and existing entities)
    if (
      businessEntityOwnerEntityOptions.length ||
      existingEntityOptions.length
    ) {
      options.push(
        ...businessEntityOwnerEntityOptions.map((option) => ({
          ...option,
          groupName: 'Entities',
        })),
        ...existingEntityOptions.map((option) => ({
          ...option,
          groupName: 'Entities',
        }))
      );
    }

    return options;
  }, [
    suggestedPrincipalOptions,
    clientProfileOptions,
    businessEntityOwnerEntityOptions,
    creatableOwnerEntityOptions,
    existingEntityOptions,
    alreadyCreatedSuggestions,
    alreadyCreatedOwnerEntityNodeNames,
  ]);

  const principalSelectionValidation = useMemo(() => {
    const validPrincipalIDs = new Set(
      richOptions
        .filter((option) => !option.onSelectAction)
        .map((option) => option.value)
    );
    return {
      validSelection: (value: unknown) =>
        hasValidPrincipalSelection(value, validPrincipalIDs) ||
        `${columns.principalIds.columnHeader} is required.`,
    };
  }, [columns.principalIds.columnHeader, richOptions]);

  // Watch the selected principal value(s)
  const watchedSelectedPrincipalIds = useWatch({
    control,
    name: `entities.${index}.principalIds`,
  });

  const selectedPrincipalIds = useMemo(
    () => watchedSelectedPrincipalIds || [],
    [watchedSelectedPrincipalIds]
  );
  const [principalInputValue, setPrincipalInputValue] = useState('');

  const selectedPrincipalId =
    useWatch({
      control,
      name: `entities.${index}.principalId`,
    }) || '';

  const showMultiSelect = allowMultiplePrincipals;

  const resolvedOwnerEntityIds = useMemo(
    () =>
      businessEntityOwnerEntityOptions
        .map((option) => option.value)
        .filter((value) => getPulidKind(value) === PulidKind.Entity),
    [businessEntityOwnerEntityOptions]
  );

  const autoSelectedResolvedOwnerEntityIdsRef = useRef<string[]>([]);

  useEffect(() => {
    autoSelectedResolvedOwnerEntityIdsRef.current = [];
  }, [suggestionID]);

  useEffect(() => {
    if (resolvedOwnerEntityIds.length === 0) return;

    // Track auto-seeded owners so the user can remove them without this effect
    // immediately re-adding them on the next selectedPrincipalIds change.
    const ownerEntityIdsToAutoSelect = resolvedOwnerEntityIds.filter(
      (id) => !autoSelectedResolvedOwnerEntityIdsRef.current.includes(id)
    );
    if (ownerEntityIdsToAutoSelect.length === 0) return;

    autoSelectedResolvedOwnerEntityIdsRef.current = [
      ...autoSelectedResolvedOwnerEntityIdsRef.current,
      ...ownerEntityIdsToAutoSelect,
    ];

    if (showMultiSelect) {
      const missingOwnerEntityIds = ownerEntityIdsToAutoSelect.filter(
        (id) => !selectedPrincipalIds.includes(id)
      );
      if (missingOwnerEntityIds.length === 0) return;

      setValue(
        `entities.${index}.principalIds` as const satisfies PathsOf<AIOnboardingModalFormShape>,
        [...selectedPrincipalIds, ...missingOwnerEntityIds]
      );
      return;
    }

    if (selectedPrincipalId) return;

    setValue(
      `entities.${index}.principalId` as const satisfies PathsOf<AIOnboardingModalFormShape>,
      first(ownerEntityIdsToAutoSelect) || ''
    );
  }, [
    index,
    resolvedOwnerEntityIds,
    selectedPrincipalId,
    selectedPrincipalIds,
    setValue,
    showMultiSelect,
  ]);

  const suggestedClientProfileWarning = useMemo(() => {
    const alreadyCreated = find(
      suggestedPrincipalOptions,
      (opt) => !alreadyCreatedSuggestions.includes(opt.value)
    );
    if (selectedPrincipalIds.length) {
      // If there are already selected principal IDs, we don't show the warning
      return undefined;
    }
    return alreadyCreated;
  }, [
    suggestedPrincipalOptions,
    selectedPrincipalIds.length,
    alreadyCreatedSuggestions,
  ]);

  // Warning for missing owner entities that are not yet created.
  const missingOwnerEntityWarning = useMemo(() => {
    if (selectedPrincipalIds.length) {
      return undefined;
    }

    const option = creatableOwnerEntityOptions.find(
      (opt) => !alreadyCreatedOwnerEntityNodeNames.includes(opt.nodeName)
    );

    return option ? { display: option.display } : undefined;
  }, [
    creatableOwnerEntityOptions,
    alreadyCreatedOwnerEntityNodeNames,
    selectedPrincipalIds.length,
  ]);

  const warningDisplayText =
    suggestedClientProfileWarning?.display ||
    missingOwnerEntityWarning?.display;

  const initialClientProfileName = pendingSuggestion?.initialClientProfileName;

  const handleAfterCreate = useCallback(
    (newId: string) => {
      diagnostics.debug('[EntityFieldsRow] onAfterCreate individual', {
        pendingSuggestion,
        newId,
        allowMultiplePrincipals,
      });

      if (!pendingSuggestion) return;

      setAlreadyCreatedSuggestions((prev) => [
        ...prev,
        pendingSuggestion.value,
      ]);

      if (allowMultiplePrincipals) {
        const updated = [...selectedPrincipalIds, newId];
        diagnostics.debug(
          '[EntityFieldsRow] onAfterCreate updating principalIds',
          {
            currentIds: selectedPrincipalIds,
            updated,
          }
        );
        setValue(
          `entities.${index}.principalIds` as const satisfies PathsOf<AIOnboardingModalFormShape>,
          updated
        );
      } else {
        diagnostics.debug(
          '[EntityFieldsRow] onAfterCreate updating principalId',
          {
            newId,
          }
        );
        setValue(
          `entities.${index}.principalId` as const satisfies PathsOf<AIOnboardingModalFormShape>,
          newId
        );
      }

      // Reset the modal and pending suggestion
      setClientProfileModalOpen(false);
      setPendingSuggestion(null);
    },
    [
      pendingSuggestion,
      allowMultiplePrincipals,
      index,
      selectedPrincipalIds,
      setValue,
    ]
  );

  // ------------------------------------------------------------
  // Create Entity modal handling ("Add <entity>")
  // ------------------------------------------------------------

  const handleAfterCreateEntity = useCallback(
    (newEntityId: string) => {
      if (!pendingEntity) return;

      diagnostics.debug('[EntityFieldsRow] onAfterCreate entity', {
        pendingEntity,
        newEntityId,
        allowMultiplePrincipals,
      });

      setAlreadyCreatedOwnerEntityNodeNames((prev) => [
        ...prev,
        pendingEntity.nodeName,
      ]);

      if (allowMultiplePrincipals) {
        const updated = [...selectedPrincipalIds, newEntityId];
        setValue(
          `entities.${index}.principalIds` as const satisfies PathsOf<AIOnboardingModalFormShape>,
          updated
        );
      } else {
        setValue(
          `entities.${index}.principalId` as const satisfies PathsOf<AIOnboardingModalFormShape>,
          newEntityId
        );
      }

      // Close modal and reset
      setEntityModalOpen(false);
      setPendingEntity(null);
    },
    [
      pendingEntity,
      allowMultiplePrincipals,
      index,
      selectedPrincipalIds,
      setValue,
    ]
  );

  return (
    <>
      <ClientProfileSideDrawer
        isOpen={isClientProfileModalOpen}
        setIsOpen={setClientProfileModalOpen}
        householdId={householdId}
        clientProfileId={null}
        clientProfileTypeDisplay="individual"
        forceGrantorIfNoClientProfileId
        initialClientProfileName={initialClientProfileName}
        createdFromSuggestionID={pendingSuggestion?.suggestionID}
        ignoredQueryDocuments={[EntityDocumentViewerDocument]}
        onAfterCreate={handleAfterCreate}
      />
      {isEntityModalOpen && pendingEntity && (
        <CreateEntityShortFormModal
          isOpen={isEntityModalOpen}
          onClose={() => {
            setEntityModalOpen(false);
          }}
          householdId={householdId}
          initialEntityName={pendingEntity?.display}
          initialEntityType={pendingEntity.entityType || undefined}
          isOnboardingFlow={false}
          onAfterCreate={handleAfterCreateEntity}
          ignoredQueryDocuments={[
            EntityDocumentViewerDocument,
            FetchAiOnboardingModalDataV2Document,
          ]}
        />
      )}
      <FormLayoutRow sx={{ gap: styles.columnPaddingHorizontal }}>
        <FormLayoutItem width={columns.entityType.columnWidth}>
          <FormAwareSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `entities.${index}.entityType` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.entityType.columnHeader}
            options={entityTypeOptions}
            hideLabel
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.entityName.columnWidth}>
          <FormAwareTextInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `entities.${index}.entityName` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.entityName.columnHeader}
            endAdornment={
              suggestionNotes ? (
                <SuggestionNotesHelp notes={suggestionNotes} />
              ) : undefined
            }
            hideLabel
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.principalIds.columnWidth}>
          {showMultiSelect ? (
            <FormAwareTypeaheadMultiSelectInput
              control={control}
              fieldName={
                `entities.${index}.principalIds` as const satisfies PathsOf<AIOnboardingModalFormShape>
              }
              label={columns.principalIds.columnHeader}
              options={richOptions}
              groupBy={(option) => option.groupName ?? ''}
              inputValue={principalInputValue}
              onInputChange={setPrincipalInputValue}
              hideLabel
              required
              validation={principalSelectionValidation}
              showErrorBeforeSubmit
              startAdornment={
                warningDisplayText ? (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <AlertTriangleFilledIcon
                      fill={COLORS.FUNCTIONAL.WARNING['500']}
                      color={COLORS.FUNCTIONAL.WARNING['500']}
                      size={16}
                    />
                    <Typography variant="body-xs" color={COLORS.GRAY['500']}>
                      {warningDisplayText}
                    </Typography>
                  </Stack>
                ) : undefined
              }
            />
          ) : (
            <FormAwareTypeaheadSelectInput<AIOnboardingModalFormShape>
              control={control}
              fieldName={
                `entities.${index}.principalId` as const satisfies PathsOf<AIOnboardingModalFormShape>
              }
              label={columns.principalIds.columnHeader}
              options={richOptions}
              groupBy={(option) => option.groupName ?? ''}
              hideLabel
              required
              validation={principalSelectionValidation}
              showErrorBeforeSubmit
            />
          )}
        </FormLayoutItem>
      </FormLayoutRow>
      <IntegrationEntityTypeaheadFieldRows
        index={index}
        rowPaddingVertical={styles.rowPaddingVertical}
        columnWidth={columns.integrationEntityIds.columnWidth}
      />
      <BasicAssetValuationFieldsRow index={index} configs={configs} />
    </>
  );
}

interface IntegrationEntityTypeaheadFieldRowProps {
  index: number;
  rowPaddingVertical: number;
  columnWidth: ColumnWidths;
}

function IntegrationEntityTypeaheadFieldRows({
  index,
  rowPaddingVertical,
  columnWidth,
}: IntegrationEntityTypeaheadFieldRowProps) {
  const { control } = useFormContext<AIOnboardingModalFormShape>();
  const {
    household: { id: householdId },
  } = useAIOnboardingModalContext();
  const { isHouseholdLinkedToAssetProvider } = useHouseholdDetailsContext();

  if (!isHouseholdLinkedToAssetProvider) {
    return null;
  }

  return (
    <FormLayoutRow sx={{ marginTop: -rowPaddingVertical }}>
      <FormLayoutItem width={columnWidth}>
        <SharedIntegrationEntityTypeahead<AIOnboardingModalFormShape>
          inputOnly
          control={control}
          filterToHouseholdId={householdId}
          fieldName={
            `entities.${index}.integrationEntityIds` as const satisfies PathsOf<AIOnboardingModalFormShape>
          }
          includeAllEntitiesFieldName={null}
        />
      </FormLayoutItem>
    </FormLayoutRow>
  );
}

interface BasicAssetValuationFieldsRowProps {
  index: number;
  configs: AIOnboardingModalFormFieldConfiguration['entities'];
}

function BasicAssetValuationFieldsRow({
  index,
  configs: { columns, styles },
}: BasicAssetValuationFieldsRowProps) {
  const {
    control,
    setValue,
    formState: { dirtyFields },
  } = useFormContext<AIOnboardingModalFormShape>();

  const { isHouseholdLinkedToAssetProvider } = useHouseholdDetailsContext();

  const { options: assetCategoryOptions } = useAssetCategoryOptions();

  const defaultAssetClassId = useDefaultAssetClassId();

  const { assetClassesById } = useTenantDetailsContext();

  const [showBasicAssetFields, entityType] = useWatch({
    control,
    name: [
      `entities.${index}._showBasicAssetFields` as const satisfies PathsOf<AIOnboardingModalFormShape>,
      `entities.${index}.entityType` as const satisfies PathsOf<AIOnboardingModalFormShape>,
    ],
  });

  // Set the default asset class ID if the asset class based on the entity type.
  useEffect(() => {
    const isAssetClassFieldDirty =
      dirtyFields?.entities?.[index]?.assetCategoryId;

    if (isAssetClassFieldDirty) return;
    const formAssetClassId = determineDefaultAssetClassId(
      entityType,
      defaultAssetClassId,
      assetClassesById
    );

    setValue(`entities.${index}.assetCategoryId`, formAssetClassId, {
      shouldDirty: false,
    });
  }, [
    assetClassesById,
    defaultAssetClassId,
    dirtyFields,
    entityType,
    index,
    setValue,
  ]);

  // Hide the entire section if household linked to asset provider
  if (isHouseholdLinkedToAssetProvider) {
    return null;
  }

  // When fields are hidden, show the add button
  if (!showBasicAssetFields) {
    return (
      <Stack direction="row" justifyContent="flex-end" sx={{ marginTop: -3 }}>
        <Button
          variant="transparent"
          size="xs"
          startIcon={PlusIcon}
          onClick={() => {
            setValue(
              `entities.${index}._showBasicAssetFields` as const satisfies PathsOf<AIOnboardingModalFormShape>,
              true
            );
          }}
        >
          Add valuation
        </Button>
      </Stack>
    );
  }

  return (
    <FormLayoutRow
      sx={{
        marginTop: -2,
        gap: styles.columnPaddingHorizontal,
        backgroundColor: COLORS.GRAY['100'],
        padding: 1,
        borderRadius: 0.75,
      }}
    >
      <FormLayoutItem width={columns.assetCategoryId.columnWidth}>
        <FormAwareSelectInput<AIOnboardingModalFormShape>
          control={control}
          fieldName={
            `entities.${index}.assetCategoryId` as const satisfies PathsOf<AIOnboardingModalFormShape>
          }
          label={columns.assetCategoryId.columnSubheader ?? ''}
          options={assetCategoryOptions}
          showEmptyValue={false}
          required
          showErrorBeforeSubmit
        />
      </FormLayoutItem>
      <FormLayoutItem width={columns.currentMarketValue.columnWidth}>
        <FormAwareCurrencyInput<AIOnboardingModalFormShape>
          isDecimalJSInput
          control={control}
          fieldName={
            `entities.${index}.currentMarketValue` as const satisfies PathsOf<AIOnboardingModalFormShape>
          }
          label={columns.currentMarketValue.columnSubheader ?? ''}
        />
      </FormLayoutItem>
      <FormLayoutItem width={columns.dateOfValuation.columnWidth}>
        <FormAwareDatePickerInput<AIOnboardingModalFormShape>
          control={control}
          fieldName={
            `entities.${index}.dateOfValuation` as const satisfies PathsOf<AIOnboardingModalFormShape>
          }
          label={columns.dateOfValuation.columnSubheader ?? ''}
        />
      </FormLayoutItem>
    </FormLayoutRow>
  );
}
